/** @jsx jsx */
import { jsx, css } from "@emotion/core";

import { graphql, Link as GatsbyLink } from "gatsby";
import { MDXRenderer } from "gatsby-plugin-mdx";

import shevy from "../styleguide/typography";
import Button from "../styleguide/button";

import DefaultLayout from "../layouts/default";
import SEO from "../components/seo";
import Wrapper from "../components/wrapper";
import ArticleDivider from "../components/article-divider";
import Article from "../components/article";
import {
    Paginator,
    PaginatorItem,
    PaginatorItemDivider,
    PaginatorLink,
} from "../components/pagination";

const BlogList = ({ data, pageContext, path }) => {
    const title =
        pageContext.currentPage > 1
            ? `Blog — Page ${pageContext.currentPage}`
            : null;

    return (
        <DefaultLayout>
            <SEO title={title} path={path} />
            <Wrapper
                css={css`
                    box-shadow: 0 8px 10px rgba(0, 0, 0, 0.06);
                    display: flex;
                    flex-direction: column;
                `}
            >
                {data.allMdx.edges.map(({ node }, i) => {
                    return (
                        <div key={node.id}>
                            <div
                                css={css`
                                    background: rgba(
                                        255,
                                        255,
                                        255,
                                        ${i % 2 ? "0.8" : "0.6"}
                                    );

                                    @media (max-width: 550px) {
                                        padding: ${shevy.baseSpacing(
                                                i === 0 ? 5 : 2
                                            )}
                                            ${shevy.baseSpacing(1)}
                                            ${shevy.baseSpacing(2)};
                                    }

                                    @media (min-width: 550px) {
                                        padding: ${shevy.baseSpacing(
                                                i === 0 ? 5 : 2
                                            )}
                                            ${shevy.baseSpacing(2)}
                                            ${shevy.baseSpacing(2)};
                                    }
                                `}
                            >
                                <Article
                                    title={node.frontmatter.title}
                                    href={node.fields.path}
                                    rawDate={node.frontmatter.rawDate}
                                    formattedDate={
                                        node.frontmatter.formattedDate
                                    }
                                    timeToRead={node.timeToRead}
                                >
                                    <MDXRenderer onlyExcerpt={true}>
                                        {node.body}
                                    </MDXRenderer>

                                    <footer
                                        css={css`
                                            margin: ${shevy.baseSpacing(1)} 0;
                                            text-align: center;
                                        `}
                                    >
                                        <Button
                                            as={GatsbyLink}
                                            to={node.fields.path}
                                        >
                                            Continue Reading »
                                        </Button>
                                    </footer>
                                </Article>
                            </div>

                            {data.allMdx.edges.length - 1 !== i && (
                                <ArticleDivider />
                            )}
                        </div>
                    );
                })}

                <Paginator>
                    <PaginatorItem>
                        {pageContext.previousPagePath && (
                            <PaginatorLink to={pageContext.previousPagePath}>
                                &larr; Newer posts
                            </PaginatorLink>
                        )}
                    </PaginatorItem>
                    <PaginatorItemDivider />
                    <PaginatorItem>
                        {pageContext.nextPagePath && (
                            <PaginatorLink to={pageContext.nextPagePath}>
                                Older posts &rarr;
                            </PaginatorLink>
                        )}
                    </PaginatorItem>
                </Paginator>
            </Wrapper>
        </DefaultLayout>
    );
};

export const blogListQuery = graphql`
    query BlogListQuery($skip: Int!, $limit: Int!) {
        allMdx(
            filter: { fields: { type: { eq: "_posts" } } }
            sort: { fields: [frontmatter___date], order: DESC }
            limit: $limit
            skip: $skip
        ) {
            edges {
                node {
                    id

                    fields {
                        path
                    }

                    frontmatter {
                        title

                        rawDate: date
                        formattedDate: date(formatString: "MMMM DD, YYYY")
                    }

                    body
                    timeToRead
                }
            }
        }
    }
`;

export default BlogList;
